import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Globe, BarChart3, ArrowRight, Search, Info, ChevronDown
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useBrand } from '@/config/brands';
import { loadStaticData } from '@/lib/offlineData';
import { getCountrySlug } from '@/lib/countryUtils';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ProcedurePrice { us_code: string; country_code: string; price_usd: number; }
interface ProcedureName { code: string; name: string; llm_quick_name?: string; }
interface USPrice { cpt_code: string; avg_gross_charge: number; }
interface CountryInfo { country_code: string; country_name: string; flag_emoji: string; }
interface OverallComparison { country_code: string; us_vs_intl_multiplier: number; scenario_count: number; }

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatUSD(n: number): string {
  if (!n || !isFinite(n)) return '—';
  if (n >= 10000) return `$${(n / 1000).toFixed(0)}k`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function ratioColor(ratio: number): string {
  // ratio = US price / intl price. Higher = US charges more = green (good deal abroad)
  if (ratio >= 50) return 'bg-green-600';
  if (ratio >= 20) return 'bg-green-500';
  if (ratio >= 10) return 'bg-emerald-500';
  if (ratio >= 5) return 'bg-emerald-400';
  if (ratio >= 3) return 'bg-yellow-500';
  if (ratio >= 2) return 'bg-yellow-400';
  if (ratio >= 1.5) return 'bg-orange-400';
  if (ratio >= 1) return 'bg-orange-500';
  return 'bg-red-500'; // intl is MORE expensive
}

function ratioTextColor(ratio: number): string {
  if (ratio >= 5) return 'text-white';
  if (ratio >= 2) return 'text-black';
  return 'text-white';
}

// Well-known high-impact procedures for the heatmap
const HEATMAP_PROCEDURES = [
  { code: '27447', label: 'Knee Replacement' },
  { code: '27130', label: 'Hip Replacement' },
  { code: '43239', label: 'Upper GI Endoscopy' },
  { code: '45380', label: 'Colonoscopy w/ Biopsy' },
  { code: '59510', label: 'C-Section Delivery' },
  { code: '66984', label: 'Cataract Surgery' },
  { code: '70553', label: 'Brain MRI w/ Contrast' },
  { code: '71046', label: 'Chest X-Ray (2 views)' },
  { code: '93000', label: 'ECG / EKG' },
  { code: '99213', label: 'Office Visit (Estab.)' },
  { code: '99214', label: 'Office Visit (Moderate)' },
  { code: '85025', label: 'CBC Blood Test' },
  { code: '80053', label: 'Metabolic Panel' },
  { code: '80061', label: 'Lipid Panel' },
  { code: '74177', label: 'CT Abdomen w/ Contrast' },
  { code: '47562', label: 'Laparoscopic Cholecystectomy' },
  { code: '29881', label: 'Knee Arthroscopy' },
  { code: '43235', label: 'Diagnostic EGD' },
  { code: '90715', label: 'Tdap Vaccine' },
  { code: '99285', label: 'ER Visit (High Severity)' },
];

const CATEGORY_FILTERS = [
  { label: 'All', filter: () => true },
  { label: 'Surgery', filter: (c: string) => { const n = parseInt(c); return n >= 10000 && n <= 69999; } },
  { label: 'Imaging', filter: (c: string) => { const n = parseInt(c); return n >= 70000 && n <= 79999; } },
  { label: 'Lab/Path', filter: (c: string) => { const n = parseInt(c); return n >= 80000 && n <= 89999; } },
  { label: 'E&M/Medicine', filter: (c: string) => { const n = parseInt(c); return n >= 90000 && n <= 99999; } },
];

// ─── Main Component ─────────────────────────────────────────────────────────

export default function InternationalHeatmap() {
  const brand = useBrand();
  const { t } = useTranslation(['international', 'common']);
  const [categoryFilter, setCategoryFilter] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const { data: intlData, isLoading } = useQuery({
    queryKey: ['international-stats-heatmap'],
    queryFn: () => loadStaticData('international_stats.json'),
    staleTime: 1000 * 60 * 60,
  });

  const countries = useMemo(() => {
    if (!intlData) return [];
    const overall = ((intlData as any).overallComparison as OverallComparison[]) || [];
    const countryList = ((intlData as any).countries as CountryInfo[]) || [];
    const countryMap = new Map(countryList.map(c => [c.country_code, c]));
    // Sort by multiplier descending, take top 16 for display
    return overall
      .filter(o => o.us_vs_intl_multiplier > 0 && isFinite(o.us_vs_intl_multiplier) && o.scenario_count >= 50)
      .sort((a, b) => b.us_vs_intl_multiplier - a.us_vs_intl_multiplier)
      .slice(0, 16)
      .map(o => ({ ...o, info: countryMap.get(o.country_code) }))
      .filter(o => o.info);
  }, [intlData]);

  const procedureNames = useMemo(() => {
    if (!intlData) return new Map<string, ProcedureName>();
    const map = new Map<string, ProcedureName>();
    ((intlData as any).procedureNames as ProcedureName[] || []).forEach(p => map.set(p.code, p));
    return map;
  }, [intlData]);

  const usPrices = useMemo(() => {
    if (!intlData) return new Map<string, number>();
    const map = new Map<string, number>();
    ((intlData as any).usPricing as USPrice[] || []).forEach(p => map.set(p.cpt_code, p.avg_gross_charge));
    return map;
  }, [intlData]);

  const intlPriceMap = useMemo(() => {
    if (!intlData) return new Map<string, Map<string, number>>();
    const map = new Map<string, Map<string, number>>();
    ((intlData as any).procedurePrices as ProcedurePrice[] || []).forEach(p => {
      if (!map.has(p.us_code)) map.set(p.us_code, new Map());
      map.get(p.us_code)!.set(p.country_code, p.price_usd);
    });
    return map;
  }, [intlData]);

  // Procedures to show in the heatmap
  const procedures = useMemo(() => {
    if (showCustom && searchQuery.length >= 2) {
      const q = searchQuery.toLowerCase();
      const results: typeof HEATMAP_PROCEDURES = [];
      procedureNames.forEach((proc) => {
        if (proc.code.toLowerCase().includes(q) || proc.name.toLowerCase().includes(q) || (proc.llm_quick_name && proc.llm_quick_name.toLowerCase().includes(q))) {
          results.push({ code: proc.code, label: proc.llm_quick_name || proc.name.substring(0, 30) });
        }
      });
      return results.slice(0, 25);
    }
    let procs = HEATMAP_PROCEDURES;
    if (categoryFilter > 0) {
      const filterFn = CATEGORY_FILTERS[categoryFilter].filter;
      procs = procs.filter(p => filterFn(p.code));
    }
    return procs;
  }, [categoryFilter, searchQuery, showCustom, procedureNames]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Global Price Heatmap — Procedures × Countries | {brand.name}</title>
        <meta name="description" content="Interactive heatmap showing healthcare price ratios across procedures and countries. See where the US overcharges the most at a glance." />
        <meta property="og:title" content="Global Healthcare Price Heatmap — US vs 32 Countries" />
        <meta property="og:description" content="See the price ratio of every procedure across 16+ countries in one visual grid." />
        <link rel="canonical" href={`${brand.primaryDomain}/international/heatmap`} />
      </Helmet>

      {/* Hero */}
      <section className="border-b border-border/40 bg-gradient-to-b from-primary/5 to-background">
        <div className="max-w-[95rem] mx-auto px-4 py-10 sm:py-14 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm mb-4">
            <BarChart3 className="w-4 h-4" />
            Interactive Price Grid
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">
            Global Price <span className="text-primary">Heatmap</span>
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
            Each cell shows how many times more the US charges compared to that country.
            Green = massive savings abroad. Red = similar or higher prices.
          </p>

          {/* Controls */}
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {CATEGORY_FILTERS.map((cat, idx) => (
              <button
                key={cat.label}
                onClick={() => { setCategoryFilter(idx); setShowCustom(false); }}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  categoryFilter === idx && !showCustom
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {cat.label}
              </button>
            ))}
            <button
              onClick={() => setShowCustom(!showCustom)}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                showCustom ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <Search className="w-3 h-3 inline mr-1" />
              Custom
            </button>
          </div>
          {showCustom && (
            <div className="max-w-md mx-auto">
              <input
                type="text"
                placeholder="Search procedures..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          )}
        </div>
      </section>

      <div className="max-w-[95rem] mx-auto px-2 sm:px-4 py-6">
        {isLoading ? (
          <div className="text-center py-20">
            <Globe className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading price data...</p>
          </div>
        ) : (
          <>
            {/* Legend */}
            <div className="flex items-center justify-center gap-1 mb-6 text-xs">
              <span className="text-muted-foreground mr-2">US charges:</span>
              {[
                { label: '50x+', color: 'bg-green-600' },
                { label: '20x', color: 'bg-green-500' },
                { label: '10x', color: 'bg-emerald-500' },
                { label: '5x', color: 'bg-emerald-400' },
                { label: '3x', color: 'bg-yellow-500' },
                { label: '2x', color: 'bg-yellow-400' },
                { label: '1.5x', color: 'bg-orange-400' },
                { label: '1x', color: 'bg-orange-500' },
                { label: '<1x', color: 'bg-red-500' },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-0.5">
                  <div className={`w-4 h-3 rounded-sm ${item.color}`} />
                  <span className="text-muted-foreground">{item.label}</span>
                </div>
              ))}
              <span className="text-muted-foreground ml-2">more</span>
            </div>

            {/* Heatmap Grid */}
            <div className="overflow-x-auto rounded-xl border border-border/40">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/30">
                    <th className="text-left p-2 pl-3 sticky left-0 bg-muted/30 z-10 min-w-[180px] font-semibold">
                      Procedure
                    </th>
                    <th className="p-2 text-right min-w-[70px] font-semibold text-red-400">
                      🇺🇸 US
                    </th>
                    {countries.map(c => (
                      <th key={c.country_code} className="p-1.5 text-center min-w-[55px]">
                        <Link to={`/international/${getCountrySlug(c.country_code)}`} className="hover:underline">
                          <span className="text-base block">{c.info?.flag_emoji}</span>
                          <span className="text-[10px] text-muted-foreground block leading-tight">{c.country_code}</span>
                        </Link>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {procedures.map((proc, idx) => {
                    const usP = usPrices.get(proc.code) || 0;
                    const intlPrices = intlPriceMap.get(proc.code);

                    return (
                      <tr key={proc.code} className={idx % 2 === 0 ? 'bg-background' : 'bg-muted/10'}>
                        <td className="p-2 pl-3 sticky left-0 bg-inherit z-10 border-r border-border/20">
                          <Link to={`/international/code-explorer/${proc.code}`} className="hover:text-primary transition-colors">
                            <span className="font-mono text-primary/70">{proc.code}</span>
                            <span className="ml-1.5 text-foreground">{proc.label}</span>
                          </Link>
                        </td>
                        <td className="p-2 text-right font-medium text-red-400 border-r border-border/20">
                          {usP > 0 ? formatUSD(usP) : '—'}
                        </td>
                        {countries.map(c => {
                          const intlP = intlPrices?.get(c.country_code);
                          if (!intlP || intlP <= 0 || !usP || usP <= 0) {
                            return (
                              <td key={c.country_code} className="p-1 text-center">
                                <div className="w-full h-8 rounded bg-muted/20 flex items-center justify-center text-muted-foreground/40">
                                  —
                                </div>
                              </td>
                            );
                          }
                          const ratio = usP / intlP;
                          return (
                            <td key={c.country_code} className="p-0.5 text-center">
                              <div
                                className={`w-full h-8 rounded flex items-center justify-center font-semibold ${ratioColor(ratio)} ${ratioTextColor(ratio)}`}
                                title={`US ${formatUSD(usP)} vs ${c.info?.country_name} ${formatUSD(intlP)} (${ratio.toFixed(1)}x)`}
                              >
                                {ratio >= 100 ? `${Math.round(ratio)}x` : ratio >= 10 ? `${ratio.toFixed(0)}x` : `${ratio.toFixed(1)}x`}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {procedures.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Search className="w-8 h-8 mx-auto mb-3 opacity-50" />
                <p>No matching procedures found</p>
              </div>
            )}

            {/* Info card */}
            <Card className="mt-8 bg-muted/30">
              <CardContent className="p-4 flex items-start gap-3">
                <Info className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <div className="text-sm text-muted-foreground">
                  <p className="mb-1">
                    <strong>How to read this:</strong> Each cell shows the ratio of US average hospital charge to that country's government fee schedule price.
                    "10x" means the US charges 10 times more. Green cells = massive savings abroad.
                  </p>
                  <p>
                    US prices from hospital chargemasters ({usPrices.size.toLocaleString()} procedures).
                    International prices from {countries.length} official government fee schedules.
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Bottom */}
        <div className="mt-12 text-center border-t border-border/40 pt-8">
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/international">
              <Button variant="outline" size="sm">
                <Globe className="w-4 h-4 mr-2" />
                International Hub
              </Button>
            </Link>
            <Link to="/international/calculator">
              <Button variant="outline" size="sm">
                <BarChart3 className="w-4 h-4 mr-2" />
                Scenario Calculator
              </Button>
            </Link>
            <Link to="/international/code-explorer">
              <Button variant="outline" size="sm">
                <Search className="w-4 h-4 mr-2" />
                Code Explorer
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
